import { Fragment, useState, type ReactNode } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Check, ChevronLeft, ChevronRight, Copy } from 'lucide-react';
import { api, type AuditEntry } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { Drawer } from '../components/ui/Drawer';
import { AUDIT_ACTIONS, AUDIT_ACTION_META, isSensitive } from '../lib/audit-actions';
import { formatDateTime, formatRelative } from '../lib/format';

export function AuditLog() {
  const [actor, setActor] = useState('');
  const [action, setAction] = useState('');
  const [applied, setApplied] = useState<{ actor?: string; action?: string }>({});
  const [openId, setOpenId] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(0);

  const query = useInfiniteQuery({
    queryKey: ['audit-log', applied],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.listAuditLog({
        limit: 50,
        ...(pageParam ? { cursor: pageParam } : {}),
        ...(applied.actor ? { actor: applied.actor } : {}),
        ...(applied.action ? { action: applied.action } : {}),
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 10_000,
  });

  // Pages are cursor-chained, so there is no total and no jumping ahead — but
  // every page already walked stays cached, which makes Newer free.
  const pages = query.data?.pages ?? [];
  const page = pages[pageIndex];
  const entries = page?.items ?? [];
  // The log is partitioned by day, so a page can come back short simply because
  // the walk backwards ran out of query budget — not because there is no more.
  const truncated = page?.truncated ?? false;
  const hasOlder = pageIndex < pages.length - 1 || query.hasNextPage;
  const selected = entries.find((e) => e.occurredAt === openId) ?? null;
  const filtering = Boolean(applied.actor || applied.action);

  const older = async () => {
    if (pageIndex === pages.length - 1) {
      const next = await query.fetchNextPage();
      if ((next.data?.pages.length ?? 0) <= pageIndex + 1) return;
    }
    setPageIndex((i) => i + 1);
  };

  const filterBy = (next: { actor?: string; action?: string }) => {
    setApplied(next);
    setPageIndex(0);
  };

  const apply = () =>
    filterBy({
      ...(actor.trim() ? { actor: actor.trim().toLowerCase() } : {}),
      ...(action ? { action } : {}),
    });

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Audit Log</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Sign-ins, refused sign-ins, every change made through the portal, and every export that
          left the platform — newest first.
        </p>
      </div>

      <form
        className="flex flex-col gap-3 sm:flex-row sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          apply();
        }}
      >
        <label className="flex flex-1 flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Actor (email)</span>
          <Input
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            placeholder="someone@xiltrixusa.com"
          />
        </label>
        <label className="flex w-64 flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Action</span>
          <Select value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">All actions</option>
            {AUDIT_ACTIONS.map((a) => (
              <option key={a} value={a}>
                {AUDIT_ACTION_META[a].label} — {a}
              </option>
            ))}
          </Select>
        </label>
        <Button type="submit">Filter</Button>
        {filtering ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setActor('');
              setAction('');
              filterBy({});
            }}
          >
            Clear
          </Button>
        ) : null}
      </form>

      <Card>
        <CardHeader>
          <CardTitle>
            {query.isLoading
              ? 'Loading…'
              : `Page ${pageIndex + 1} · ${entries.length} event${entries.length === 1 ? '' : 's'}`}
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {query.error ? (
            <div className="text-sm text-destructive">
              Failed to load: {(query.error as Error).message}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-muted-foreground">
                  <th className="px-2 py-2 font-medium">When</th>
                  <th className="px-2 py-2 font-medium">Actor</th>
                  <th className="px-2 py-2 font-medium">Action</th>
                  <th className="px-2 py-2 font-medium">Target</th>
                  <th className="px-2 py-2 font-medium">Detail</th>
                  <th className="w-6" />
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr
                    key={e.occurredAt}
                    onClick={() => setOpenId(e.occurredAt)}
                    className="cursor-pointer border-b border-line-soft align-top transition-colors hover:bg-raise"
                  >
                    <td className="px-2 py-2 text-xs whitespace-nowrap text-muted-foreground tabular-nums">
                      {formatDateTime(e.timestamp)}
                    </td>
                    <td className="px-2 py-2 font-mono text-xs">{e.actor}</td>
                    <td className="px-2 py-2">
                      <ActionBadge action={e.action} outcome={e.outcome} />
                    </td>
                    <td className="px-2 py-2 font-mono text-xs">{e.target || '—'}</td>
                    <td className="px-2 py-2 text-xs text-muted-foreground">
                      <Summary entry={e} />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <ChevronRight className="inline h-3.5 w-3.5 text-muted-foreground" />
                    </td>
                  </tr>
                ))}
                {!query.isLoading && entries.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-2 py-6 text-center text-muted-foreground">
                      No events{filtering ? ' match that filter' : ''}.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          )}

          {truncated ? (
            <p className="mt-3 text-xs text-muted-foreground">
              Stopped partway through the day range to bound the query. Older keeps walking back.
            </p>
          ) : null}

          {pageIndex > 0 || hasOlder ? (
            <div className="mt-4 flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => setPageIndex((i) => i - 1)}
                disabled={pageIndex === 0}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Newer
              </Button>
              <Button
                variant="outline"
                onClick={() => void older()}
                disabled={!hasOlder || query.isFetchingNextPage}
              >
                {query.isFetchingNextPage ? 'Loading…' : 'Older'}
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Drawer
        open={selected !== null}
        onClose={() => setOpenId(null)}
        title={selected ? <EntryTitle entry={selected} /> : null}
        footer={selected ? <CopyJson entry={selected} /> : undefined}
      >
        {selected ? <EntryDetail entry={selected} /> : null}
      </Drawer>
    </div>
  );
}

function ActionBadge({ action, outcome }: { action: string; outcome: string }) {
  const variant =
    outcome === 'denied' || outcome === 'error'
      ? 'destructive'
      : isSensitive(action)
        ? 'warning'
        : action.startsWith('login')
          ? 'outline'
          : 'secondary';
  return (
    <Badge variant={variant} className="font-mono text-[11px]">
      {action}
    </Badge>
  );
}

/**
 * The operator's stated reason, when the action took one (gateway commands do).
 * It is the part of the record a reviewer reads first, so it is pulled out of
 * the generic details rather than left as one comma-joined field among many.
 */
function reasonOf(entry: AuditEntry): string | null {
  const reason = entry.details?.['reason'];
  return typeof reason === 'string' && reason.trim() ? reason : null;
}

function Summary({ entry }: { entry: AuditEntry }) {
  const reason = reasonOf(entry);
  const details = Object.entries(entry.details ?? {}).filter(([k]) => k !== 'reason');
  if (reason || details.length > 0) {
    return (
      <span>
        {reason && (
          <>
            <b>Reason:</b> {reason}
            {details.length > 0 && <span className="text-muted-foreground"> · </span>}
          </>
        )}
        {details.map(([k, v]) => `${k}: ${fmt(v)}`).join(', ')}
      </span>
    );
  }
  return <span>{entry.sourceIp ? `from ${entry.sourceIp}` : '—'}</span>;
}

function EntryTitle({ entry }: { entry: AuditEntry }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="truncate font-mono text-[15px] font-semibold">{entry.action}</span>
        {entry.outcome !== 'success' ? (
          <Badge
            variant={entry.outcome === 'denied' || entry.outcome === 'error' ? 'destructive' : 'outline'}
            className="shrink-0 text-[11px]"
          >
            {entry.outcome}
          </Badge>
        ) : null}
      </div>
      <div className="truncate pt-0.5 text-[13px] text-muted-foreground">{entry.actor}</div>
    </div>
  );
}

// The drawer is the whole record: every field the table abbreviates or drops
// entirely (route, request id, user agent) shows here in full.
function EntryDetail({ entry }: { entry: AuditEntry }) {
  const reason = reasonOf(entry);
  const details = Object.entries(entry.details ?? {}).filter(([k]) => k !== 'reason');
  const relative = formatRelative(entry.timestamp);

  return (
    <div className="flex flex-col gap-4">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="text-muted-foreground">When</dt>
        <dd className="min-w-0">
          {formatDateTime(entry.timestamp)}
          {relative ? <span className="text-muted-foreground"> · {relative}</span> : null}
          <div className="font-mono text-xs break-all text-muted-foreground">{entry.timestamp}</div>
        </dd>

        <dt className="text-muted-foreground">Actor</dt>
        <dd className="min-w-0 font-mono text-[13px] break-all">
          {entry.actor}
          <span className="text-muted-foreground"> ({entry.actorRole})</span>
        </dd>

        <dt className="text-muted-foreground">Action</dt>
        <dd className="min-w-0 font-mono text-[13px] break-all">{entry.action}</dd>

        <dt className="text-muted-foreground">Outcome</dt>
        <dd
          className={
            entry.outcome === 'denied' || entry.outcome === 'error' ? 'text-destructive' : 'text-ok'
          }
        >
          {entry.outcome}
        </dd>

        <dt className="text-muted-foreground">Target</dt>
        <dd className="min-w-0 font-mono text-[13px] break-all">{entry.target || '—'}</dd>

        <dt className="text-muted-foreground">Route</dt>
        <dd className="min-w-0 font-mono text-[13px] break-all">{entry.route || '—'}</dd>

        <dt className="text-muted-foreground">Request id</dt>
        <dd className="min-w-0 font-mono text-xs break-all text-muted-foreground">
          {entry.requestId}
        </dd>
      </dl>

      {reason ? (
        <div>
          <SectionLabel>Reason</SectionLabel>
          <p className="rounded-md border border-line bg-panel-2 px-3 py-2 text-sm break-words">{reason}</p>
        </div>
      ) : null}

      {details.length > 0 ? (
        <div>
          <SectionLabel>Detail</SectionLabel>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-sm">
            {details.map(([k, v]) => (
              <Fragment key={k}>
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="min-w-0 font-mono text-[13px] break-all">{fmt(v)}</dd>
              </Fragment>
            ))}
          </dl>
        </div>
      ) : null}

      {entry.sourceIp || entry.userAgent ? (
        <div>
          <SectionLabel>Origin</SectionLabel>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-sm">
            {entry.sourceIp ? (
              <Fragment>
                <dt className="text-muted-foreground">Source IP</dt>
                <dd className="min-w-0 font-mono text-[13px] break-all">{entry.sourceIp}</dd>
              </Fragment>
            ) : null}
            {entry.userAgent ? (
              <Fragment>
                <dt className="text-muted-foreground">User agent</dt>
                <dd className="min-w-0 text-xs break-all text-muted-foreground">
                  {entry.userAgent}
                </dd>
              </Fragment>
            ) : null}
          </dl>
        </div>
      ) : null}
    </div>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="pb-1.5 text-[12px] font-semibold tracking-[0.09em] text-muted-foreground uppercase">
      {children}
    </div>
  );
}

function CopyJson({ entry }: { entry: AuditEntry }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(JSON.stringify(entry, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button variant="outline" size="sm" onClick={() => void copy()}>
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy JSON'}
    </Button>
  );
}

function fmt(v: unknown): string {
  if (v === undefined || v === null) return '—';
  if (Array.isArray(v)) return v.length ? `[${v.join(', ')}]` : '[]';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
